// Encodes and decodes a set of picks as an opaque token for a shareable link.
//
// The token carries chosen sets only: no identity, no ratings, no notes, no name.
// It belongs in the URL fragment, which is never sent to a server.
//
// Sets are named by day, stage and start time rather than by position in the
// bill, so a token survives reordering and resolves to nothing when a set moves.
//
// Version 1 layout:
//   byte 0        format version
//   byte 1..4n    one four byte record per pick: day, stage, hour, minute

const VERSION = 1;
const RECORD = 4;

// The parts of a set this encoding reads.
export interface LinkableSet {
  id: string;
  dayId: string;
  stageId: string;
}

export interface PlanLinkCatalog {
  // Day identifiers in a fixed order. The order is part of the format.
  days: string[];
  // Stage identifiers in a fixed order.
  stages: string[];
  sets: LinkableSet[];
}

export interface DecodedPlan {
  setIds: string[];
  // Count of records naming a set absent from the catalog.
  missing: number;
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(token: string): Uint8Array | null {
  const normalized = token.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  try {
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

// Reads the start hour and minute off the trailing two segments of a set id,
// which is how the identifier is built. Independent of the reader's time zone.
function startOf(set: LinkableSet): { hour: number; minute: number } | null {
  const parts = set.id.split('-');
  const minute = Number(parts[parts.length - 1]);
  const hour = Number(parts[parts.length - 2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 255 || minute < 0 || minute > 255) return null;
  return { hour, minute };
}

// Encodes set identifiers against the catalog, dropping any the catalog lacks.
export function encodePlan(setIds: string[], catalog: PlanLinkCatalog): string {
  const byId = new Map(catalog.sets.map((s) => [s.id, s]));
  const dayIndex = new Map(catalog.days.map((d, i) => [d, i]));
  const stageIndex = new Map(catalog.stages.map((s, i) => [s, i]));

  const records: number[][] = [];
  // Sorting and deduplicating make one plan produce one token.
  for (const id of [...new Set(setIds)].sort()) {
    const set = byId.get(id);
    if (!set) continue;
    const day = dayIndex.get(set.dayId);
    const stage = stageIndex.get(set.stageId);
    const start = startOf(set);
    if (day === undefined || stage === undefined || start === null) continue;
    if (day > 255 || stage > 255) continue;
    records.push([day, stage, start.hour, start.minute]);
  }

  const bytes = new Uint8Array(1 + records.length * RECORD);
  bytes[0] = VERSION;
  records.forEach((record, i) => bytes.set(record, 1 + i * RECORD));
  return toBase64Url(bytes);
}

// Resolves a token against the catalog. Returns null for an unreadable token,
// and otherwise the set identifiers found plus a count of those absent.
export function decodePlan(token: string, catalog: PlanLinkCatalog): DecodedPlan | null {
  const bytes = fromBase64Url(token.trim());
  if (!bytes || bytes.length === 0) return null;
  if (bytes[0] !== VERSION) return null;
  if ((bytes.length - 1) % RECORD !== 0) return null;

  const wanted = new Map<string, string>();
  for (const set of catalog.sets) {
    const start = startOf(set);
    if (start === null) continue;
    const day = catalog.days.indexOf(set.dayId);
    const stage = catalog.stages.indexOf(set.stageId);
    if (day < 0 || stage < 0) continue;
    wanted.set(`${day}.${stage}.${start.hour}.${start.minute}`, set.id);
  }

  const setIds: string[] = [];
  let missing = 0;
  for (let offset = 1; offset < bytes.length; offset += RECORD) {
    const key = `${bytes[offset]}.${bytes[offset + 1]}.${bytes[offset + 2]}.${bytes[offset + 3]}`;
    const id = wanted.get(key);
    if (id === undefined) missing++;
    else if (!setIds.includes(id)) setIds.push(id);
  }

  return { setIds, missing };
}

// Builds the full share URL, keeping the token in the fragment.
export function planLinkUrl(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, '')}/shared#p=${token}`;
}

// Extracts the token from a location fragment, with or without a leading hash.
export function tokenFromHash(hash: string): string {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const part of raw.split('&')) {
    if (part.startsWith('p=')) return part.slice(2);
  }
  return '';
}
